import Route from '@ember/routing/route'
import { service } from '@ember/service'
import type { IntlService } from 'ember-intl'
import type ReportingService from 'teacher-ui-client/services/reporting'
import { restartableTask, timeout } from 'ember-concurrency'
import { Product } from 'teacher-ui-client/utils/products'
import { toRequestScopeStudent } from 'teacher-ui-client/services/reporting'
import type { RequestScope, ReportingQueryPage, ReportingQuerySort } from 'teacher-ui-client/services/reporting'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type ReadingEggspressDashboardStudentRoute from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/route'
import type {
  AssignmentsOverviewReportData,
  AssignmentsEventsReportData,
} from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/index/route'
import { ExportType } from 'teacher-ui-client/components/reporting/export-select'
import type FileDownload from 'teacher-ui-client/services/file-download'
import { formatExcel } from 'teacher-ui-client/utils/dates'
import { reportingFilename } from 'teacher-ui-client/utils/filename'
import type { Params as AssignmentsIndexParams } from 'teacher-ui-client/reporting/ui-scope/rex/dashboard/student/assignments/index/route'
import type { NormalizedInterval } from 'date-fns'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'

export default class extends Route {
  @service reporting!: ReportingService

  @service intl!: IntlService

  @service fileDownload!: FileDownload

  @service flashQueue!: FlashQueueService

  buildRouteInfoMetadata() {
    return {
      pageTitle: this.intl.t('pages.reporting.readingEggspress.dashboard.student.assignments.title'),
      exportOptions: [
        {
          exportType: ExportType.CSV,
          exportAction: () => this.exportCsv(),
        },
        { exportType: ExportType.print },
      ],
    }
  }

  model() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const { student } = this.modelFor(
      'reporting.ui-scope.rex.dashboard.student',
    ) as ModelFor<ReadingEggspressDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)

    void this.fetchAssignmentsOverviewReportTask.perform(Product.REX, period, requestScope).catch(() => {})

    // performing the events task is left up to the sub-route, where it has page/sort query params.
    // this is done in a sub-route to ensure the tasks in this model are not recalled when those QPs change.

    return {
      period,
      requestScope,
      fetchAssignmentsOverviewReportTask: this.fetchAssignmentsOverviewReportTask,
      fetchAssignmentsEventsReportTask: this.fetchAssignmentsEventsReportTask,
    }
  }

  fetchAssignmentsOverviewReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchAssignmentsOverviewReport>) => {
      await timeout(200)
      return this.fetchAssignmentsOverviewReport(...args)
    },
  )

  fetchAssignmentsEventsReportTask = restartableTask(
    async (...args: Parameters<typeof this.fetchAssignmentsEventsReport>) => {
      await timeout(200)
      return this.fetchAssignmentsEventsReport(...args)
    },
  )

  fetchAssignmentsOverviewReport(product: Product, period: NormalizedInterval, requestScope: RequestScope) {
    const reportName = 'student-dashboard/assignments/overview'
    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })
    const query = this.reporting.buildQuery({ period })
    return this.reporting.fetchReport<AssignmentsOverviewReportData>(url, query)
  }

  fetchAssignmentsEventsReport(
    product: Product,
    sort: ReportingQuerySort,
    page: ReportingQueryPage,
    period: NormalizedInterval,
    requestScope: RequestScope,
  ) {
    const reportName = 'student-dashboard/assignments/events'
    const url = this.reporting.buildGravityUrl({ product, requestScope, reportName })
    const query = this.reporting.buildQuery({ sort, page, period })
    return this.reporting.fetchReport<AssignmentsEventsReportData>(url, query)
  }

  async exportCsv() {
    const { period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>
    const { student } = this.modelFor(
      'reporting.ui-scope.rex.dashboard.student',
    ) as ModelFor<ReadingEggspressDashboardStudentRoute>
    const requestScope = toRequestScopeStudent(student.id)
    const { sort } = this.paramsFor(
      'reporting.ui-scope.rex.dashboard.student.spelling-lesson-progress.index',
    ) as AssignmentsIndexParams
    const page = 'all'

    let overviewReport: Awaited<ReturnType<typeof this.fetchAssignmentsOverviewReport>>
    let eventsReport: Awaited<ReturnType<typeof this.fetchAssignmentsEventsReport>>
    try {
      overviewReport = await this.fetchAssignmentsOverviewReport(Product.REX, period, requestScope)
      eventsReport = await this.fetchAssignmentsEventsReport(Product.REX, sort, page, period, requestScope)
    } catch {
      this.flashQueue.addFail({ subtitle: this.intl.t('failedToExportReport') })
      return
    }

    const overviewHeader = [
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.totalAssignmentsCompleted'),
      `${this.intl.t(
        'components.reporting.tables.readingEggspress.dashboardStudent.headers.averageAssignmentScore',
      )} %`,
    ]
    const overviewRow = [
      overviewReport.overview.total_assignments_completed,
      overviewReport.overview.average_assignment_score,
    ]

    const eventHeader = [
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.date'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.assignmentType'),
      this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.lessonName'),
      `${this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.headers.score')} %`,
    ]
    const eventRows = eventsReport.events.map((ev) => {
      let lessonName
      if (ev.task_type === 'book') {
        lessonName = ev.task_name
        if (ev.task_chapter) {
          lessonName += ` - ${this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.chapter', {
            chapter: ev.task_chapter,
          })}`
        }
      } else {
        lessonName = this.intl.t('components.reporting.tables.readingEggspress.dashboardStudent.lessonEventRow', {
          number: ev.task_lesson,
          name: ev.task_name,
        })
      }
      return [
        formatExcel(new Date(ev.created_at)),
        this.intl.t(`components.reporting.tables.readingEggspress.dashboardStudent.taskType.${ev.task_type}`),
        lessonName,
        ev.score,
      ]
    })

    const overviewFilename = `${reportingFilename('assignments-overview', student.fullName, period)}.csv`
    const eventsFilename = `${reportingFilename('assignments-recent-activity', student.fullName, period)}.csv`

    this.fileDownload.downloadAsCsv([overviewHeader, overviewRow], { filename: overviewFilename, header: true })
    this.fileDownload.downloadAsCsv([eventHeader, ...eventRows], { filename: eventsFilename, header: true })
  }
}
